import { useEffect } from 'react'
import { isEqual } from 'lodash'

import { CardType, COLLECTION_STATUS_NAME } from 'types/deck'

export type Filters = Record<FilterType, Record<string, boolean>>
export type FilterType =
  | 'keywords'
  | 'types'
  | 'colors'
  | 'manaValues'
  | 'collection'
  | 'colorTags'
  | 'categories'
  | 'layouts'

export const defaultFilter: Filters = {
  'keywords': {},
  'types': {},
  'collection': {},
  'colors': {},
  'colorTags': {},
  'categories': {},
  'manaValues': {},
  'layouts': {},
}

const cardToString = (cardFace: CardType) => {
  let text = cardFace.name

  text += cardFace.superTypes.reduce((acc, type) => (acc += ` ${type} `), '')
  text += cardFace.types.reduce((acc, type) => (acc += ` ${type} `), '')
  text += cardFace.subTypes.reduce((acc, type) => (acc += ` ${type} `), '')
  text += `${cardFace.text} `
  text += cardFace.castingCost.map(cost => `{${cost}}`).join('')

  return text
}

export const useCardFilter = (
  filters: Filters,
  searchValue: string,
  cards: CardType[],
  setFilteredCardIds: (cardIds: string[]) => void,
) => {
  useEffect(() => {
    const filteredCardNames = new Array<string>()

    if (isEqual(filters, defaultFilter) && searchValue.length < 2) {
      setFilteredCardIds([])

      return
    }

    for (const card of cards) {
      const cardText = card.front && card.back ? cardToString(card.front) + cardToString(card.back) : cardToString(card)

      if (searchValue.length > 2 && !cardText.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) continue

      const cardTypes = [
        ...card.types,
        ...card.subTypes,
        ...card.superTypes,
        ...(card.front?.types || []),
        ...(card.front?.superTypes || []),
        ...(card.front?.subTypes || []),
        ...(card.back?.types || []),
        ...(card.back?.superTypes || []),
        ...(card.back?.subTypes || []),
      ]

      const filteringKeywords = Object.values(filters.keywords).some(status => status)
      const filteringCategories = Object.values(filters.categories).some(status => status)
      const filteringColors = Object.values(filters.colors).some(status => status)
      const filteringTypes = Object.values(filters.types).some(status => status)
      const filteringManaValues = Object.values(filters.manaValues).some(status => status)
      const filteringCollectionStatus = Object.values(filters.collection).some(status => status)
      const filteringColorTag = Object.values(filters.colorTags).some(status => status)
      const filteringLayout = Object.values(filters.layouts).some(layout => layout)

      // prettier-ignore
      const colorMatch = card.colors.some(color => filters.colors?.[color]) || (card.colors.length === 0 && filters.colors.Colorless)
      const keywordMatch = card.keywords.some(keyword => filters.keywords?.[keyword])
      const categoryMatch = card.categories.some(category => filters.categories?.[category])
      const typeMatch = cardTypes.some(type => filters.types?.[type])

      const manaValueMatch = filters.manaValues?.[`${card.cmc}`]
      const collectionStatusMatch = !!filters.collection?.[COLLECTION_STATUS_NAME[card.owned]]
      const colorTagMatch = !!filters.colorTags?.[card.colorLabel.name]
      const layoutMatch = !!filters.layouts?.[card.layout]

      if (filteringKeywords && !keywordMatch) continue
      if (filteringCategories && !categoryMatch) continue
      if (filteringColors && !colorMatch) continue
      if (filteringTypes && !typeMatch) continue
      if (filteringManaValues && !manaValueMatch) continue
      if (filteringCollectionStatus && !collectionStatusMatch) continue
      if (filteringColorTag && !colorTagMatch) continue
      if (filteringLayout && !layoutMatch) continue

      filteredCardNames.push(card.name)
    }

    setFilteredCardIds(filteredCardNames)
  }, [filters, searchValue, JSON.stringify(cards)])
}
